import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import { Select } from "../shared/select.component.tsx";
import { objectivesForPatient } from "./clinical-snippets.ts";

export interface Objective {
  id: string;
  category: "dentaire" | "squelettique" | "esthetique" | "fonctionnel";
  label: string;
  detail?: string;
  progress: number; // 0–100
  status: "pending" | "active" | "achieved";
  priority: "haute" | "normale" | "basse";
}

const CAT_META: Record<
  Objective["category"],
  { label: string; color: string; bg: string; icon: string }
> = {
  squelettique: {
    label: "Squelettique",
    color: "text-violet-700",
    bg: "bg-violet-50 border-violet-200",
    icon: "Bone",
  },
  dentaire: {
    label: "Dentaire",
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
    icon: "Target",
  },
  esthetique: {
    label: "Esthétique",
    color: "text-pink-700",
    bg: "bg-pink-50 border-pink-200",
    icon: "Star",
  },
  fonctionnel: {
    label: "Fonctionnel",
    color: "text-green-700",
    bg: "bg-green-50 border-green-200",
    icon: "Activity",
  },
};

const PRIO_META: Record<
  Objective["priority"],
  { label: string; color: string }
> = {
  haute: { label: "Haute", color: "text-red-600 bg-red-50 border-red-200" },
  normale: {
    label: "Normale",
    color: "text-amber-700 bg-amber-50 border-amber-200",
  },
  basse: {
    label: "Basse",
    color: "text-slate-500 bg-slate-50 border-slate-200",
  },
};

function progressColor(p: number, status: Objective["status"]) {
  if (status === "achieved") return "bg-green-500";
  if (p >= 75) return "bg-sf-primary";
  if (p >= 40) return "bg-amber-400";
  return "bg-red-400";
}

function ObjectiveRow({ obj }: { obj: Objective }) {
  const cat = CAT_META[obj.category];
  const prio = PRIO_META[obj.priority];
  const isAchieved = obj.status === "achieved";
  const isPending = obj.status === "pending";

  return (
    <div
      className={cn(
        "group rounded-lg border px-3 py-2.5 transition-all",
        isAchieved
          ? "border-green-200 bg-green-50/60"
          : isPending
            ? "border-sf-border bg-sf-surface-alt/50 opacity-70"
            : "border-sf-border bg-white hover:border-sf-primary/30 hover:shadow-sm",
      )}
    >
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                "rounded border px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide",
                cat.bg,
                cat.color,
              )}
            >
              {cat.label}
            </span>
            {obj.priority === "haute" && (
              <span
                className={cn(
                  "rounded border px-1.5 py-0.5 text-[9.5px] font-semibold",
                  prio.color,
                )}
              >
                ↑ {prio.label}
              </span>
            )}
            {isAchieved && (
              <span className="rounded border border-green-300 bg-green-100 px-1.5 py-0.5 text-[9.5px] font-semibold text-green-700">
                ✓ Atteint
              </span>
            )}
          </div>
          <div
            className={cn(
              "mt-1 text-[12.5px] font-semibold",
              isAchieved ? "text-green-800 line-through" : "text-sf-text",
            )}
          >
            {obj.label}
          </div>
          {obj.detail && (
            <div className="mt-0.5 text-[11px] text-sf-text-muted">
              {obj.detail}
            </div>
          )}
        </div>
        <span className="shrink-0 text-[12px] font-bold text-sf-text-secondary">
          {obj.progress}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-sf-surface-2">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-700",
            progressColor(obj.progress, obj.status),
          )}
          style={{ width: `${obj.progress}%` }}
        />
      </div>
    </div>
  );
}

// Composant interne réutilisable — widget dashboard ET page clinique
export function ObjectivesContent() {
  const { current } = Interactions.usePatient();
  const editsRef = React.useRef<Map<string, Objective[]>>(new Map());
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  const [showAdd, setShowAdd] = React.useState(false);
  const [newLabel, setNewLabel] = React.useState("");
  const [newCat, setNewCat] = React.useState<Objective["category"]>("dentaire");
  const [filter, setFilter] = React.useState<Objective["status"] | "all">(
    "all",
  );
  const toast = Interactions.useToast();

  const seed = React.useMemo<Objective[]>(
    () => (current ? objectivesForPatient(current.uuid) : []),
    [current?.uuid],
  );
  const objectives = current
    ? (editsRef.current.get(current.uuid) ?? seed)
    : [];
  const setObjectives = (updater: (prev: Objective[]) => Objective[]) => {
    if (!current) return;
    const prev = editsRef.current.get(current.uuid) ?? seed;
    editsRef.current.set(current.uuid, updater(prev));
    force();
  };

  const visible = React.useMemo(
    () =>
      filter === "all"
        ? objectives
        : objectives.filter((o) => o.status === filter),
    [objectives, filter],
  );

  const achieved = objectives.filter((o) => o.status === "achieved").length;
  const total = objectives.length;
  const overallProgress =
    total === 0
      ? 0
      : Math.round(objectives.reduce((s, o) => s + o.progress, 0) / total);

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    const next: Objective = {
      id: `o-${Date.now()}`,
      category: newCat,
      label: newLabel.trim(),
      progress: 0,
      status: "pending",
      priority: "normale",
    };
    setObjectives((prev) => [next, ...prev]);
    setNewLabel("");
    setShowAdd(false);
    toast.push({ type: "success", title: "Objectif ajouté" });
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Synthèse globale */}
      <div className="rounded-lg border border-sf-border bg-gradient-to-br from-sf-primary-50 to-white px-4 py-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-sf-primary/80">
            Progression globale
          </div>
          <span className="text-[14px] font-bold text-sf-primary">
            {overallProgress}%
          </span>
        </div>
        <div className="mb-2 h-2 overflow-hidden rounded-full bg-sf-primary-100">
          <div
            className="h-full rounded-full bg-sf-primary transition-all duration-700"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <div className="flex gap-4 text-[11px] text-sf-text-muted">
          <span>
            <strong className="text-green-700">{achieved}</strong> atteint
            {achieved > 1 ? "s" : ""}
          </span>
          <span>
            <strong className="text-sf-primary">
              {objectives.filter((o) => o.status === "active").length}
            </strong>{" "}
            en cours
          </span>
          <span>
            <strong className="text-slate-500">
              {objectives.filter((o) => o.status === "pending").length}
            </strong>{" "}
            à planifier
          </span>
        </div>
      </div>

      {/* Filtres + ajout */}
      <div className="flex items-center gap-1.5">
        {(
          [
            { k: "all", l: "Tous" },
            { k: "active", l: "En cours" },
            { k: "achieved", l: "Atteints" },
            { k: "pending", l: "À planifier" },
          ] as const
        ).map(({ k, l }) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={cn(
              "rounded border px-2 py-0.5 text-[11px] font-medium transition-colors",
              filter === k
                ? "border-sf-primary bg-sf-primary text-white"
                : "border-sf-border bg-white text-sf-text-secondary hover:bg-sf-surface-alt",
            )}
          >
            {l}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="inline-flex h-6 w-6 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-sf-primary hover:text-white hover:border-sf-primary transition-colors"
          title="Ajouter un objectif"
        >
          <I.Plus size={13} />
        </button>
      </div>

      {/* Formulaire ajout */}
      {showAdd && (
        <div className="flex flex-col gap-2 rounded-lg border border-sf-primary-100 bg-sf-primary-50 p-3">
          <div className="flex gap-2">
            <input
              autoFocus
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
                if (e.key === "Escape") setShowAdd(false);
              }}
              placeholder="Libellé de l'objectif…"
              className="min-w-0 flex-1 rounded border border-sf-border bg-white px-2 py-1.5 text-[12.5px] outline-none focus:border-sf-primary"
            />
            <Select
              value={newCat}
              onChange={(v) => setNewCat(v as Objective["category"])}
              options={(
                Object.entries(CAT_META) as [
                  Objective["category"],
                  (typeof CAT_META)[keyof typeof CAT_META],
                ][]
              ).map(([k, v]) => ({ value: k, label: v.label }))}
              className="w-32"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAdd}
              className="flex-1 rounded bg-sf-primary py-1.5 text-[12px] font-semibold text-white hover:opacity-90"
            >
              Ajouter
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="rounded border border-sf-border bg-white px-3 py-1.5 text-[12px] text-sf-text-muted hover:bg-sf-surface-alt"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="flex flex-col gap-2">
        {visible.length === 0 && (
          <div className="py-6 text-center text-[12px] text-sf-text-muted">
            Aucun objectif dans cette catégorie.
          </div>
        )}
        {visible.map((obj) => (
          <ObjectiveRow key={obj.id} obj={obj} />
        ))}
      </div>
    </div>
  );
}

// Widget dashboard (wrapper avec guard patient courant)
export function ObjectivesWidget() {
  const { current } = Interactions.usePatient();

  if (!current) {
    return (
      <div className="flex h-full min-h-24 flex-col items-center justify-center rounded-xl border border-dashed border-sf-border py-8 text-center">
        <I.Target size={20} className="mb-2 text-sf-text-muted" />
        <p className="text-[12.5px] text-sf-text-muted">
          Sélectionnez un patient
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sf-text-muted">
        <I.Target size={12} />
        Objectifs de traitement
      </div>
      <ObjectivesContent />
    </div>
  );
}
